import {
  genericForwardRefMemo,
  genericMemo,
} from "@redotech/react-util/component";
import { useTranslation } from "@redotech/redo-i18n";
import classNames from "classnames";
import {
  ForwardedRef,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Flex, FlexProps } from "../../flex";
import { Text, TextProps } from "../../text";
import { OptionalTooltip } from "../../tooltip/optional-tooltip";
import { RedoBadge, RedoBadgeProps } from "../badge/redo-badge";
import { RedoCommandMenu } from "../command-menu/redo-command-menu";
import * as horizontalTabsCss from "./redo-horizontal-tabs.module.css";

export const redoHorizontalTabSize = ["xs", "sm"] as const;
export type RedoHorizontalTabSize = (typeof redoHorizontalTabSize)[number];

export const redoHorizontalTabMode = ["regular", "full-width"] as const;
export type RedoHorizontalTabMode = (typeof redoHorizontalTabMode)[number];

interface OverflowableBucketItem<T> {
  item: T;
  mustNotBeBucketed?: boolean;
}

/**
 *
 * @param availableSpaceRef - The space that the items can be shown in.
 * @param visibleItemsRef - The items that can be shown.
 * @param moreItemsButtonRef - The button that opens the overflow menu.
 */
function useOverflowableBucket<T>(
  allItems: OverflowableBucketItem<T>[],
  availableSpaceRef: HTMLDivElement | null,
  visibleItemsRef: HTMLDivElement | null,
  moreItemsButtonRef: HTMLDivElement | null,
  itemsHash: (items: OverflowableBucketItem<T>[]) => string,
) {
  const [availableSpaceWidth, setAvailableSpaceWidth] = useState<number>(0);
  const [itemsInNormalSpot, setItemsInNormalSpot] = useState<T[]>(
    allItems.map((item) => item.item),
  );
  const [itemsInBucket, setItemsInBucket] = useState<T[]>([]);

  const [shouldUpdateItems, setShouldUpdateItems] = useState<boolean>(false);
  const [prevItemHash, setPrevItemHash] = useState<string | number | null>(
    itemsHash(allItems),
  );

  const PADDING_BETWEEN_ITEMS = 10;

  const DEFAULT_BUTTON_WIDTH = 10;

  const updateAvailableSpaceWidth = useCallback(() => {
    if (!availableSpaceRef) {
      return;
    }
    if (
      availableSpaceRef.offsetWidth !== availableSpaceWidth ||
      itemsHash(allItems) !== prevItemHash
    ) {
      setAvailableSpaceWidth(availableSpaceRef.offsetWidth);
      setItemsInNormalSpot(allItems.map((item) => item.item));
      setPrevItemHash(itemsHash(allItems));
      setShouldUpdateItems(true);
    }
  }, [
    availableSpaceRef,
    availableSpaceWidth,
    allItems,
    itemsHash,
    prevItemHash,
  ]);

  useLayoutEffect(() => {
    if (!shouldUpdateItems) {
      return;
    }

    if (!availableSpaceRef || !visibleItemsRef) {
      return;
    }
    setShouldUpdateItems(false);

    /** Because of the effects, we're at this point guaranteed to have _all_ the items rendered, so we can calculate their widths. */

    /**
     * Left inset (tab holder `pl`) before the first item, measured rather than
     * assumed so a smaller `pl` doesn't reserve phantom space and over-bucket.
     */
    const horizontalInset = Math.max(
      0,
      visibleItemsRef.getBoundingClientRect().left -
        availableSpaceRef.getBoundingClientRect().left,
    );

    /** Start by filling up normal spot with mandatory items */
    let usedWidth = horizontalInset;

    type AugmentedItem = OverflowableBucketItem<T> & {
      width: number;
      index: number;
    };

    const allItemsAugmentedWithWidthAndIndex: AugmentedItem[] = allItems.map(
      (item, index) => ({
        ...item,
        width:
          (visibleItemsRef.children[index]?.clientWidth ??
            DEFAULT_BUTTON_WIDTH) + (index > 0 ? PADDING_BETWEEN_ITEMS : 0),
        index,
      }),
    );

    const bucketWidth = moreItemsButtonRef?.clientWidth ?? DEFAULT_BUTTON_WIDTH;

    const mandatoryItems = allItemsAugmentedWithWidthAndIndex.filter(
      (item) => item.mustNotBeBucketed,
    );
    const nonMandatoryItems = allItemsAugmentedWithWidthAndIndex.filter(
      (item) => !item.mustNotBeBucketed,
    );

    const widthRequiredForEverything = [
      ...mandatoryItems,
      ...nonMandatoryItems,
    ].reduce((acc, item) => acc + item.width, horizontalInset);

    const widthRequiredForMandatoryItems = mandatoryItems.reduce(
      (acc, item) => acc + item.width,
      0,
    );
    usedWidth += widthRequiredForMandatoryItems;

    const shouldBucket = widthRequiredForEverything > availableSpaceWidth;

    if (!shouldBucket) {
      setItemsInNormalSpot(allItems.map((item) => item.item));
      setItemsInBucket([]);
      return;
    }

    usedWidth += bucketWidth;

    const bucketedItems = nonMandatoryItems
      .sort((a, b) => a.index - b.index)
      .reduce((acc, item) => {
        if (usedWidth + item.width > availableSpaceWidth) {
          acc.push(item);
          return acc;
        }
        usedWidth += item.width;
        return acc;
      }, [] as AugmentedItem[]);

    const normalSpotItems = allItemsAugmentedWithWidthAndIndex.filter(
      (item) => !bucketedItems.includes(item),
    );

    setItemsInNormalSpot(
      normalSpotItems
        .sort((a, b) => a.index - b.index)
        .map((item) => item.item),
    );
    setItemsInBucket(
      bucketedItems.sort((a, b) => a.index - b.index).map((item) => item.item),
    );
  }, [
    shouldUpdateItems,
    availableSpaceRef,
    visibleItemsRef,
    moreItemsButtonRef,
    setItemsInNormalSpot,
    setItemsInBucket,
    allItems,
    availableSpaceWidth,
  ]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useLayoutEffect(() => {
    if (!availableSpaceRef || !visibleItemsRef) {
      return;
    }
    const observer = new ResizeObserver(updateAvailableSpaceWidth);
    observer.observe(availableSpaceRef);
    return () => observer.disconnect();
  }, [updateAvailableSpaceWidth, availableSpaceRef, visibleItemsRef]);

  useEffect(() => {
    updateAvailableSpaceWidth();
  }, []);

  return { itemsInNormalSpot, itemsInBucket };
}

export interface RedoHorizontalTab<T> {
  label: string;
  key: T;
  icon?: ReactNode;
  count?: number;
  badgeText?: string;
  badgeColor?: RedoBadgeProps["color"];
  isDisabled?: boolean;
  tooltip?: string;
  dataTestId?: string;
}

function horizontalTabsHash<T extends string>(
  items: OverflowableBucketItem<RedoHorizontalTab<T>>[],
) {
  return items
    .map(
      (item, index) =>
        `${index}${item.item.key}${item.item.label}${item.item.count ?? 0}${item.item.badgeText ?? null}${item.mustNotBeBucketed ? "s" : null}${item.item.isDisabled ? "d" : null}${item.item.tooltip ?? null}`,
    )
    .join(",");
}

interface SingleTabProps<T extends string> {
  tab: RedoHorizontalTab<T>;
  isSelected: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  setSelectedTab?: (tabKey: T) => void;
  mode: RedoHorizontalTabMode;
  size: RedoHorizontalTabSize;
  ref?: ForwardedRef<HTMLDivElement>;
}

const SingleTab = genericForwardRefMemo(function SingleTab<T extends string>(
  {
    tab,
    isSelected,
    isDisabled = false,
    tooltip,
    setSelectedTab,
    mode,
    size,
  }: SingleTabProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <OptionalTooltip
      meetsCondition={!!tooltip}
      tooltipProps={{ title: tooltip, placement: "bottom" }}
    >
      <Flex
        align="center"
        aria-selected={isSelected}
        as="button"
        basis={tabModeToTabBasis[mode]}
        className={classNames(
          horizontalTabsCss.tab,
          horizontalTabsCss[tabModeToStyleName[mode]],
          isSelected && horizontalTabsCss.selected,
          isDisabled && !isSelected && horizontalTabsCss.disabled,
        )}
        data-testid={tab.dataTestId}
        dir="row"
        justify="center"
        onClick={(e) => {
          e.preventDefault();
          if (!isDisabled) {
            setSelectedTab?.(tab.key);
          }
        }}
        pb={tabsSizeToPadding[size]}
        px="xs"
        ref={ref}
        role="tab"
        shrink={1}
      >
        {tab.icon && <span style={{ marginRight: "-4px" }}>{tab.icon}</span>}
        <Text
          fontSize={tabsSizeToTextSize[size]}
          fontWeight="semibold"
          textColor={tabSelectedToTextColor(isSelected)}
        >
          {tab.label}
        </Text>
        {tab.count ? (
          <RedoBadge
            color="gray"
            size={tabsSizeToBadgeSize[size]}
            text={tab.count.toString()}
          />
        ) : null}
        {tab.badgeText ? (
          <RedoBadge
            color={tab.badgeColor ?? "gray"}
            size={tabsSizeToBadgeSize[size]}
            text={tab.badgeText}
          />
        ) : null}
      </Flex>
    </OptionalTooltip>
  );
});

/**
 * Arbiter horizontal tabs https://www.figma.com/design/iZHj2I36zd9i8nRbWKw4ZK/%E2%9D%96-Arbiter?node-id=1547-265252&t=6E6sxbCjrl7Bw2G1-0
 */
export const RedoHorizontalTabs = genericMemo(function RedoHorizontalTabs<
  T extends string = string,
>({
  size = "xs",
  mode = "regular",
  tabs = [],
  selectedTab,
  setSelectedTab,
  pl = "6xl",
  drawBottomLine = true,
  preventMoreTab,
  dataTestId,
  contentDataTestId,
}: {
  size?: RedoHorizontalTabSize;
  mode?: RedoHorizontalTabMode;
  tabs?: RedoHorizontalTab<T>[];
  selectedTab?: T;
  setSelectedTab?: (tabKey: T) => void;
  pl?: FlexProps["pl"];
  drawBottomLine?: boolean;
  preventMoreTab?: boolean;
  dataTestId?: string;
  contentDataTestId?: string;
}) {
  const { t } = useTranslation();
  const [bucketMenuOpen, setBucketMenuOpen] = useState(false);

  const [availableSpaceRef, setAvailableSpaceRef] =
    useState<HTMLDivElement | null>(null);
  const [visibleItemsRef, setVisibleItemsRef] = useState<HTMLDivElement | null>(
    null,
  );
  const [moreItemsButtonRef, setMoreItemsButtonRef] =
    useState<HTMLDivElement | null>(null);

  const { itemsInNormalSpot, itemsInBucket } = useOverflowableBucket(
    tabs.map((tab) => ({
      item: tab,
      mustNotBeBucketed: tab.key === selectedTab || preventMoreTab,
    })),
    availableSpaceRef,
    visibleItemsRef,
    moreItemsButtonRef,
    horizontalTabsHash,
  );

  if (tabs.length === 0) {
    return null;
  }

  const moreItemsButton =
    itemsInBucket.length > 0 ? (
      <>
        <SingleTab<T>
          isSelected={false}
          mode={mode}
          ref={setMoreItemsButtonRef}
          setSelectedTab={() => {
            setBucketMenuOpen(true);
          }}
          size={size}
          tab={{
            count: itemsInBucket.length,
            label: t("More"),
            key: "more" as T,
          }}
        />
        <RedoCommandMenu
          anchor={moreItemsButtonRef}
          items={itemsInBucket.map((tab) => ({
            text: `${tab.label}${tab.count ? ` (${tab.count})` : ""}`,
            disabled: tab.isDisabled,
            key: tab.key,
            onClick: () => {
              if (!tab.isDisabled) {
                setSelectedTab?.(tab.key);
              }
            },
          }))}
          open={bucketMenuOpen}
          setOpen={setBucketMenuOpen}
        />
      </>
    ) : null;

  /**
   * We're going to build some logic for making a bucket tab that stores overflowed tabs, and has a "..." with a menu that opens.
   **/

  return (
    <Flex
      align="flex-start"
      as="div"
      data-arbiter="RedoHorizontalTabs"
      data-testid={dataTestId}
      grow={1}
      ref={setAvailableSpaceRef}
      style={{ overflow: "hidden", minWidth: 0, flexShrink: 0 }}
    >
      <Flex
        align="flex-start"
        as="div"
        basis="100%"
        className={classNames(
          horizontalTabsCss.container,
          drawBottomLine && horizontalTabsCss.lined,
        )}
        grow={1}
        justify={tabHolderFlexJustify[mode]}
      >
        <Flex
          className={horizontalTabsCss.tabHolder}
          grow={1}
          pl={pl}
          role="tablist"
        >
          <Flex
            data-testid={contentDataTestId}
            ref={setVisibleItemsRef}
            shrink={0}
          >
            {itemsInNormalSpot.map((tab, index) => {
              const isSelected = tab.key === selectedTab;
              return (
                <SingleTab<T>
                  isDisabled={tab.isDisabled}
                  isSelected={isSelected}
                  key={index}
                  mode={mode}
                  setSelectedTab={setSelectedTab}
                  size={size}
                  tab={tab}
                  tooltip={tab.tooltip}
                />
              );
            })}
            {moreItemsButton}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

const tabHolderFlexJustify: Record<
  RedoHorizontalTabMode,
  FlexProps["justify"]
> = { ["regular"]: "flex-start", ["full-width"]: "center" };

const tabModeToStyleName: Record<RedoHorizontalTabMode, string> = {
  ["regular"]: "regular",
  ["full-width"]: "fullWidth",
};

const tabModeToTabBasis: Record<RedoHorizontalTabMode, string> = {
  ["regular"]: "auto",
  ["full-width"]: "240px",
};

const tabsSizeToTextSize: Record<RedoHorizontalTabSize, TextProps["fontSize"]> =
  { ["xs"]: "xs", ["sm"]: "sm" };

const tabsSizeToPadding: Record<RedoHorizontalTabSize, FlexProps["pb"]> = {
  ["xs"]: "xs",
  ["sm"]: "lg",
};

const tabsSizeToBadgeSize: Record<
  RedoHorizontalTabSize,
  RedoBadgeProps["size"]
> = { ["xs"]: "xs", ["sm"]: "sm" };

const tabSelectedToTextColor = (selected: boolean) =>
  selected ? "primary" : "tertiary";
